import React, { useEffect, useState, useRef } from 'react';
import Axios from "axios";
import CustomInput from './CustomInput';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { setSectionFields } from '../../../store/actions/surveyFieldDataAction';
import Svg from '../../../../common/Svg';

const Section = ({
    questions,
    nextSection,
    previousSection,
    sectionId,
    handleSubmitSurvey,
    name,
    desc,
    startCount,
    ss_id,
    survey_id,
    submitResponse,
    setCurrentSection,
    setCurrentSectionWidth,
    progressBar,
    buttonHeading,
    emailValidationTiming,
    subLoading
}) => {

    // State
    const dispatch = useDispatch();
    const { surveyFieldsData } = useSelector((state) => state?.survey?.surveyFieldData);
    const [fielledRatio, setFielledRatio] = useState(0);
    const [verifyFlag, setVerifyFlag] = useState(1);
    const [otp, setOtp] = useState('');
    const [otpError, setOtpError] = useState(false);
    const [checkOtp, setCheckOtp] = useState('');

    // Yup Schema
    let schemaShape = {};

    questions?.forEach((que) => {
        const isRequired = que.su_is_required === 1;
        const key = que.su_question_id;

        if (isRequired) {
            schemaShape[key] = Yup.string().required('Required');
        } else {
            schemaShape[key] = Yup.string();
        }
    });

    // Formik Setup
    let initialValues = {};
    let initialTouched = {};
    let initialError = {};

    questions?.forEach((question) => {
        const key = question.su_question_id;
        initialValues[key] = '';
        initialTouched[key] = false;
        initialError[key] = '';
    });

    const Schema = Yup.object().shape(schemaShape);

    const formik = useFormik({
        initialValues: initialValues,
        initialErrors: initialError,
        initialTouched: initialTouched,
        validationSchema: Schema,
        onSubmit: (values) => {
            if (emailValidationTiming === 'after' && !nextSection) {

                Axios.post(`${process.env.REACT_APP_API_URL}/frnt/sendEmailVerifyOtp`, {
                    lead_id: ss_id
                })
                    .then((response) => {
                        setVerifyFlag(2);
                    })
                    .catch((error) => {
                        console.error("Action before submission failed", error);
                    });
            } else {
                if (nextSection) {
                    dispatch(setSectionFields(sectionId, values));
                    setCurrentSection(nextSection);
                } else {
                    handleSubmitSurvey();
                }
            }
        },
    });

    const otpVerify = () => {
        if (otp.trim() === '') {
            setOtpError(true);
            setCheckOtp('Please enter the OTP');
            return;
        }

        Axios.post(`${process.env.REACT_APP_API_URL}/frnt/verify-email-otp`, {
            lead_id: ss_id,
            otp: otp
        })
            .then((response) => {
                if (response.data.data.success === true) {
                    hideModal()
                    handleSubmitSurvey();
                } else {
                    setOtpError(true);
                    setCheckOtp('Invalid OTP');
                }
            })
            .catch((error) => {
                console.error("Error signing up:", error);
                setOtpError(true);
                setCheckOtp('Error verifying OTP. Please try again.');
            });
    };

    const { values, handleSubmit, setValues } = formik;

    // Functionalities
    const handlePageChangeToPrev = (e, prevSection) => {
        e.preventDefault();
        setCurrentSection(prevSection);
    };



    function showModal() {
        const modalElement = document.getElementById('otpModal');
        if (modalElement) {
            modalElement.style.display = 'block';
            document.body.classList.add('modal-open');

            // Create backdrop
            const backdrop = document.createElement('div');
            backdrop.className = 'modal-backdrop fade show';
            document.body.appendChild(backdrop);

            // Add show class after a small delay to trigger transition
            setTimeout(() => {
                modalElement.classList.add('show');
            }, 100);
        } else {
            console.warn('Modal element not found');
        }
    }

    // Function to hide the modal
    function hideModal() {
        const modalElement = document.getElementById('otpModal');
        if (modalElement) {
            modalElement.classList.remove('show');

            // Remove modal and backdrop after transition
            setTimeout(() => {
                modalElement.style.display = 'none';
                document.body.classList.remove('modal-open');
                const backdrop = document.querySelector('.modal-backdrop');
                if (backdrop) {
                    backdrop.remove();
                }
            }, 300); // Adjust this timeout to match your transition duration
        }
    }

    useEffect(() => {
        if (verifyFlag === 2) {
            const modalElement = document.getElementById('otpModal');
            if (modalElement) {
                const modalInstance = new window.bootstrap.Modal(modalElement, {
                    backdrop: 'static',
                    keyboard: false
                });
                modalInstance.show();
            }
        }

        // if (verifyFlag === 2) {
        //     showModal();
        // }
    }, [verifyFlag]);



    // Effects
    useEffect(() => {
        if (values) {
            dispatch(setSectionFields(sectionId, values));
        }
    }, [values]);

    const elementRef = useRef(null);

    useEffect(() => {
        // Reset scroll to top first. Using window.scrollTo instead of
        // scrollIntoView to avoid cross-frame scrolling issues in iframes.
        window.scrollTo(0, 0);

        const getOffsetFromTop = () => {
            if (elementRef.current) {
                const rect = elementRef.current.getBoundingClientRect();
                const offsetFromTop = rect.top + window.scrollY;
                window.scrollTo({ top: (offsetFromTop - 153) });
            }
        };

        // Wait one frame so the new section DOM is fully rendered before
        // calculating the offset position.
        requestAnimationFrame(() => {
            getOffsetFromTop();
        });

        window.addEventListener('resize', getOffsetFromTop);

        if (surveyFieldsData && surveyFieldsData[sectionId]) {
            setValues(surveyFieldsData[sectionId]);
        }

        return () => {
            window.removeEventListener('resize', getOffsetFromTop);
        };
    }, [sectionId]);

    useEffect(() => {
        if (values && questions) {
            const totalQuestion = questions.length;
            let totalAnsweredQuestion = 0;

            Object.keys(values).forEach((que) => {
                if (values[que]) {
                    totalAnsweredQuestion += 1;
                }
            });

            const fieldedQueRatio = totalAnsweredQuestion / totalQuestion;
            setFielledRatio(fieldedQueRatio);
            setCurrentSectionWidth(fieldedQueRatio);
        }
    }, [values, questions]);

    return (
        <div className="container" ref={elementRef}>
            {verifyFlag === 1 && (
                <>
                    <div className='header pt-3 section-title-question'>
                        <h1 className='fw-600 fs-38'>{name ?? ''}</h1>
                        <p>{desc ?? ''}</p>
                    </div>
                    <form onSubmit={handleSubmit}>
                        <div className="question-list">
                            {questions?.map((question, index) => (
                                <div key={index} id={`qusDiv_${question.su_question_id}`}>
                                    <CustomInput
                                        fieldData={question}
                                        formikData={formik}
                                        submitResponseCallback={submitResponse}
                                        numbering={startCount + index}
                                    />
                                </div>
                            ))}
                        </div>
                        <div className="btn-wrapper text-end d-md-flex justify-content-between">
                            {previousSection && (
                                <button className='btn btn-link btn-icon' onClick={(e) => { handlePageChangeToPrev(e, previousSection) }}>
                                    <Svg name="back-button-icon" /> Back
                                </button>
                            )}
                            {nextSection ? (
                                <button
                                    className={`btn btn-primary btn-icon ms-auto d-inline-flex ${fielledRatio !== 1 ? 'btn-disabled' : ''}`}
                                    disabled={fielledRatio !== 1}
                                    type='submit'
                                >
                                    Next <Svg name="button-icon" />
                                </button>
                            ) : (
                                subLoading ? (
                                    <button
                                        className="btn btn-primary btn-icon"
                                        type="submit"
                                        disabled
                                    >

                                        {"Loading..."} <div className="spinner-border text-light" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                    </button>
                                ) : (
                                    <button
                                        className={`btn btn-primary btn-icon ms-auto ${fielledRatio !== 1 ? 'btn-disabled' : ''}`}
                                        disabled={fielledRatio !== 1}
                                        type='submit'
                                    // data-bs-toggle={emailValidationTiming === 'after' ? "modal" : ""}
                                    // data-bs-target={emailValidationTiming === 'after' ? "#otpModal" : ""}
                                    >
                                        {buttonHeading} <Svg name="button-icon" />
                                    </button>
                                )
                            )}
                        </div>
                    </form>
                </>
            )}
            {verifyFlag === 2 && (
                <div className="modal fade verifyOtpModal" id="otpModal" tabIndex="-1" aria-labelledby="exampleModalLabel" aria-hidden="true">
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">

                            <div className="modal-body">
                                <div className="heading-wrapper">
                                    <h1>Verify email</h1>
                                    <p>We've sent a 6-digit verification code to your email address. Please enter it below to verify your email.</p>
                                </div>

                                <div className="form-wrapper">
                                    <div className="form-floating mb-4">
                                        <input
                                            className="form-control"
                                            id="otp"
                                            name="otp"
                                            type="text"
                                            placeholder="Enter OTP"
                                            value={otp}
                                            onChange={(e) => {
                                                setOtp(e.target.value);
                                                setOtpError(false);
                                            }}
                                            required
                                            inputMode="numeric"
                                            maxLength={6}
                                            onKeyPress={(e) => {
                                                const pattern = /^[0-9\b]+$/;
                                                if (!pattern.test(e.key)) {
                                                    e.preventDefault();
                                                }
                                            }}
                                        />
                                        <label htmlFor="otp">OTP</label>
                                        {otpError && <div className="error model-error">{checkOtp}</div>}
                                    </div>
                                </div>
                                <div className='d-flex justify-content-start'>
                                    <button type="button" className="btn btn-primary btn-icon" onClick={otpVerify}>Verify <Svg name="button-icon" /></button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Section;
